import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

interface IOptions<T extends FieldValues> {
	expectDirtyField?: FieldPath<T>;
	/**
	 * Typical behavior: form submit is disabled when a form is internally invalid, even though the individual errors are not shown.
	 * Sometimes it is needed to enable the submit despite the form not being valid internally. Use case: multi-step forms
	 * */
	whenInternallyInvalid?: boolean;
}

export const getSubmitDisabled = <T extends FieldValues>(
	formCtx: UseFormReturn<T>,
	options: IOptions<T> = {}
) => {
	const {
		isSubmitting,
		errors,
		dirtyFields,
		isValid: isInternalValid,
	} = formCtx.formState;

	if (options.whenInternallyInvalid && !isInternalValid) {
		return true;
	}

	/**
	 * TODO `dirtyFields[options.expectDirtyField]` won't work in case the `expectDirtyField` is a complex key, e.g: "formState.groupA.fieldA"
	 * the type assertion "as keyof typeof dirtyFields" is also inaccurate
	 * */
	if (
		options.expectDirtyField &&
		!dirtyFields[options.expectDirtyField as keyof typeof dirtyFields]
	) {
		return true;
	}

	/**
	 * This one is used intentionally in favor of formCtx.formState.isValid
	 * Mainly because the {errors} array is not always in sync with formCtx.formState.isValid,
	 * but we want to disable the button only when the validation errors are visible
	 * */
	const isValid = Object.keys(errors).length === 0;

	return isSubmitting || !isValid;
};
